import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';

/**
 * One **live** session — a device that is signed in.
 *
 * A session *is* its current refresh-token row. Refresh tokens rotate: every
 * refresh revokes the presented row and mints its successor, chained through
 * `replaced_by_token_id`. So the live rows of an account are exactly its
 * signed-in devices, one row each, and the rotated ones behind them are history
 * rather than sessions — listing those would let an operator revoke a token that
 * was already superseded and believe they had ended something.
 *
 * `id` therefore names the *current* row and changes on every refresh. It is
 * still the right handle for a revoke: the older rows of the family are already
 * revoked, and the successor cannot exist yet.
 *
 * **Never** the token or its hash (N2) — this contract has no field to put one in.
 *
 * There is deliberately **no `isCurrent`**. Marking the caller's own session
 * requires identifying it from the request, and a staff request carries nobody
 * else's refresh token — the field would be `false` on every row of the only
 * surface that exists today, which a UI would render as a badge meaning something
 * it does not mean. P11 adds it honestly, together with the member's own session
 * list, by stamping the minting session's id into the access token.
 */
export const SessionSchema = z.object({
  id: UuidSchema,
  /**
   * When the current row was minted — i.e. the device's **last refresh**, not
   * when it signed in. Rotation makes this the best available "last active"
   * signal, and it is named for what it is: the session's start is not
   * recoverable without walking a rotation chain that grows with every refresh.
   */
  lastActiveAt: z.iso.datetime(),
  /** When this row stops being usable — the session dies here unless refreshed. */
  expiresAt: z.iso.datetime(),
  /** As reported by the device on its last refresh. Recorded, never trusted. */
  userAgent: z.string().nullable(),
  ip: z.string().nullable(),
});

export type Session = z.infer<typeof SessionSchema>;
